import React from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { useNavTrail } from '../../Layouts/LayoutMenuData';

const isSame = (a, b) =>
  String(a || '')
    .trim()
    .toLowerCase() ===
  String(b || '')
    .trim()
    .toLowerCase();

/**
 * In-page breadcrumb: Home > Group > Module > [Page] > [parents] > current.
 *
 * Group, module and page come from the sidebar, so the trail always agrees
 * with the menu. Everything before the last crumb is a link when it has a
 * target. List pages pass nothing and the module is the current page; detail
 * and form pages pass `current` (the record) and the module links back.
 *
 * @param {string} [current]  label of the page being viewed
 * @param {{label: string, to?: string}[]} [parents]  extra levels between the
 *   module and `current` (e.g. a detail page above a sub-page)
 * @param {{label: string, to: string}} [fallback]  module to show when the
 *   sidebar has no match (menu still loading, or hidden for this role)
 * @param {string} [listClassName]  extra class on the <ol>
 */
const PageBreadcrumb = ({ current, parents, fallback, listClassName }) => {
  const trail = useNavTrail();

  const crumbs = [];
  if (trail) {
    if (trail.group?.label && !isSame(trail.group.label, trail.module.label)) {
      crumbs.push({ label: trail.group.label });
    }
    crumbs.push({ label: trail.module.label, to: trail.module.link });
    if (trail.page) {
      crumbs.push({ label: trail.page.label, to: trail.page.link });
    }
  } else if (fallback) {
    crumbs.push(fallback);
  }
  (parents || []).forEach(p => crumbs.push(p));
  if (current) crumbs.push({ label: current });

  return (
    <Breadcrumb listClassName={listClassName}>
      <BreadcrumbItem>
        <Link to='/dashboard'>Home</Link>
      </BreadcrumbItem>
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return (
          <BreadcrumbItem key={`${index}-${crumb.label}`} active={isLast}>
            {!isLast && crumb.to ? (
              <Link to={crumb.to}>{crumb.label}</Link>
            ) : (
              crumb.label
            )}
          </BreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );
};

export default PageBreadcrumb;
